import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { UserAvatar } from '@/entities/user';

import {
  DashboardTrashIcon,
  MagicIcon,
  PictureIcon,
  ScanIcon,
} from '@/shared/assets';
import { useDisclosure } from '@/shared/lib';
import {
  Button,
  DashboardDialog,
  DashboardDialogProps,
  ScrollArea,
  Typography,
} from '@/shared/ui';

import { ChangeAvatarStep } from '../model/types';
import { useAvatarStore } from '../model/useAvatarStore.ts';
import { AvatarsList } from './avatars-list';
import { ResetModal } from './reset-modal';

interface EditProfilePhotoProps {
  onSuccess?: () => void;
}
const EditProfilePhotoContent = ({ onSuccess }: EditProfilePhotoProps) => {
  const { userAvatar, setUserAvatar, testAvatar, setTestAvatar } =
    useAvatarStore(useShallow((state) => state));
  const onSave = () => {
    setUserAvatar(testAvatar);
    onSuccess?.();
  };
  const needSave = testAvatar !== userAvatar;
  const onRemove = () => {
    setTestAvatar('');
  };
  return (
    <ScrollArea className="max-h-[500px]">
      <div className="flex flex-col items-center pb-1 pt-2">
        <UserAvatar
          variant="xl"
          className="mb-4 border border-graphite"
          avatar={testAvatar}
          defaultAvatar={testAvatar ? !userAvatar : undefined}
          indicatorPosition="top"
          indicatorClassName="!bg-contrast-500 cursor-pointer border-base-500"
          indicator={<DashboardTrashIcon />}
          showIndicator={!!testAvatar}
          onIndicatorClick={onRemove}
          isLink={false}
        />
        <div className="flex w-fit items-center gap-x-1">
          <Button
            variant="base-300"
            className="w-[100px] flex-col gap-y-4 rounded-3xl px-2 py-4"
          >
            <PictureIcon className="min-h-6 min-w-6" />
            <Typography className="text-wrap">Choose photo</Typography>
          </Button>
          <Button
            variant="base-300"
            className="w-[100px] flex-col gap-y-4 rounded-3xl px-2 py-4"
          >
            <ScanIcon className="min-h-6 min-w-6" />
            <Typography className="text-wrap">Take photo</Typography>
          </Button>
        </div>
        <AvatarsList
          onSelectAvatar={setTestAvatar}
          testAvatar={testAvatar}
          className="mt-4"
        />
        {needSave && (
          <Button
            variant="contrast-500"
            size="d-l"
            className="absolute bottom-1 w-full"
            onClick={onSave}
          >
            Save changes
          </Button>
        )}
      </div>
    </ScrollArea>
  );
};
export const EditProfilePhotoModal = () => {
  const { userAvatar, step, setStep, testAvatar, reset } = useAvatarStore(
    useShallow((state) => state),
  );
  const needSave = testAvatar !== userAvatar;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const handleClose = () => {
    if (needSave) {
      setStep('reset');
    }
    if (step === 'reset') {
      reset();
      onClose();
    }
    if (!needSave) {
      onClose();
    }
  };
  const handleOpenChange = (open: boolean) => {
    if (open) {
      onOpen();
    } else {
      handleClose();
    }
  };
  const changeAvatarFlow: Record<ChangeAvatarStep, DashboardDialogProps> = {
    select: {
      title: 'profile photo',
      animationProps: {
        isFirst: true,
        id: 'edit-profile-photo',
      },
      handleOnly: true,
      content: (
        <EditProfilePhotoContent
          onSuccess={() => {
            onClose();
            toast.success('Profile photo updated');
          }}
        />
      ),
    },
    reset: {
      hideCloseButton: true,
      header: <></>,
      content: (
        <ResetModal
          onConfirm={() => {
            reset();
            onClose();
          }}
          onCancel={() => {
            setStep('select');
          }}
        />
      ),
      animationProps: {
        id: 'reset',
      },
    },
  };
  return (
    <DashboardDialog
      contentClassName="py-0"
      open={isOpen}
      onOpenChange={handleOpenChange}
      {...changeAvatarFlow[step]}
    >
      <MagicIcon />
    </DashboardDialog>
  );
};
